/* Little Cubbie — Meaningful Play redesign. Shared behaviour for lc-* sections. */
(() => {
  /* Scroll reveal */
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('is-on'); io.unobserve(e.target); }
      }), { threshold: .14 })
    : null;
  const initReveal = root => (root || document).querySelectorAll('.lc-reveal:not(.is-on)').forEach(el => io ? io.observe(el) : el.classList.add('is-on'));
  document.addEventListener('DOMContentLoaded', () => initReveal());
  document.addEventListener('shopify:section:load', e => initReveal(e.target));
  if (document.readyState !== 'loading') initReveal();

  /* Soft "pop" sound shared by floating toys */
  let audio, lastPop = 0;
  const AC = window.AudioContext || window.webkitAudioContext;
  const enableSound = () => { if (!AC) return; audio ||= new AC(); audio.resume(); };
  window.addEventListener('pointerdown', enableSound, { once: true, passive: true });
  window.addEventListener('keydown', enableSound, { once: true });
  const pop = () => {
    const now = performance.now();
    if (now - lastPop < 110 || !audio || audio.state !== 'running') return;
    lastPop = now;
    const t = audio.currentTime, o = audio.createOscillator(), g = audio.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(430, t);
    o.frequency.exponentialRampToValueAtTime(850, t + .075);
    g.gain.setValueAtTime(.0001, t);
    g.gain.exponentialRampToValueAtTime(.045, t + .012);
    g.gain.exponentialRampToValueAtTime(.0001, t + .11);
    o.connect(g).connect(audio.destination);
    o.start(t); o.stop(t + .115);
  };

  /* Floating toy stage: toys float, rotate to new characters, react to hover and link to their product */
  class LcToyStage extends HTMLElement {
    connectedCallback() {
      let toys = [];
      try { toys = JSON.parse(this.querySelector('script[type="application/json"]').textContent); } catch (e) { return; }
      this.slots = Array.from(this.querySelectorAll('.lc-toy'));
      if (!toys.length || !this.slots.length) return;
      const sound = this.dataset.sound === 'true';
      const shuffled = toys.map(t => [Math.random(), t]).sort((a, b) => a[0] - b[0]).map(x => x[1]);
      this.slots.forEach((slot, i) => {
        if (shuffled[i]) this.set(slot, shuffled[i]); else slot.hidden = true;
        if (sound) { slot.addEventListener('pointerenter', pop); slot.addEventListener('focus', pop); }
      });
      this.pinUnderNote();
      const every = Number(this.dataset.interval || 4.2) * 1000;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || toys.length <= this.slots.length || every <= 0) return;
      let n = 0;
      this.timer = setInterval(() => {
        const visible = new Set(this.slots.map(s => s.dataset.name));
        const pool = toys.filter(t => !visible.has(t.name));
        const slot = this.slots[n++ % this.slots.length];
        if (!pool.length || slot.hidden || slot.matches(':hover, :focus-visible')) return;
        const next = pool[Math.floor(Math.random() * pool.length)];
        slot.classList.add('is-changing');
        setTimeout(() => {
          this.set(slot, next);
          slot.classList.remove('is-changing');
          slot.classList.add('has-arrived');
          setTimeout(() => slot.classList.remove('has-arrived'), 550);
        }, 360);
      }, every);
    }
    /* Keeps toy 2 clear of the photos: in the gap beside the handwritten arrow on desktop, beside the price badge on phones. */
    pinUnderNote() {
      const toy = this.querySelector('.lc-toy--2');
      const hero = this.closest('.lc-hero');
      const q = s => hero && hero.querySelector(s);
      const type = q('.lc-hero__type'), script = q('.lc-hero__script'), badge = q('.lc-hero__price'), cards = q('.lc-hero__cards');
      if (this.dataset.pinNote !== 'true' || !toy || !type || !cards) return;
      const phone = window.matchMedia('(max-width: 850px)');
      const size = h => { toy.style.width = h ? `${Math.round(h * .78)}px` : ''; toy.querySelectorAll('img').forEach(i => { i.style.height = h ? `${h}px` : ''; }); };
      const place = () => {
        const stage = this.getBoundingClientRect();
        toy.style.right = 'auto';
        if (phone.matches && badge) {
          size(0);
          const b = badge.getBoundingClientRect();
          toy.style.left = `${b.right - stage.left + 18}px`;
          toy.style.top = `${b.top - stage.top + b.height / 2 - toy.offsetHeight / 2}px`;
          return;
        }
        const t = type.getBoundingClientRect(), c = cards.getBoundingClientRect();
        const from = (script || type).getBoundingClientRect().right;
        const to = badge ? badge.getBoundingClientRect().left : stage.right;
        const h = Math.min(150, c.top - t.bottom + 30, (to - from - 20) / .78);
        if (h < 60) { size(0); ['left', 'right', 'top'].forEach(p => { toy.style[p] = ''; }); return; }
        size(h);
        toy.style.left = `${(from + to) / 2 - stage.left - (h * .78) / 2}px`;
        toy.style.top = `${(t.bottom + c.top) / 2 - stage.top - h / 2}px`;
      };
      this.placeToy = place;
      place();
      this.resizer = new ResizeObserver(place);
      this.resizer.observe(hero);
    }
    disconnectedCallback() { clearInterval(this.timer); this.resizer?.disconnect(); }
    set(slot, toy) {
      slot.href = toy.url || '#';
      slot.dataset.name = toy.name;
      slot.setAttribute('aria-label', `${toy.name} — shop this toy`);
      slot.innerHTML = `<img src="${toy.image}" alt="${toy.name} Little Cubbie toy" loading="eager"><span>${toy.name}</span>`;
      if (this.placeToy && slot.classList.contains('lc-toy--2')) this.placeToy();
    }
  }
  if (!customElements.get('lc-toy-stage')) customElements.define('lc-toy-stage', LcToyStage);

  /* Product page: gallery thumbnails, quantity stepper, coupon copy, variant picker */
  const updateBuyLabel = form => {
    const btn = form && form.querySelector('[data-lc-label]');
    if (!btn || btn.disabled) return;
    const qty = Math.max(1, parseInt(form.querySelector('input[name="quantity"]')?.value, 10) || 1);
    const unit = Number(btn.dataset.lcUnit || 0) / 100;
    const money = new Intl.NumberFormat('en-IN', { style: 'currency', currency: btn.dataset.lcCurrency || 'INR', maximumFractionDigits: 0 }).format(unit * qty);
    btn.textContent = btn.dataset.lcLabel.replace('[price]', money);
  };
  document.addEventListener('click', e => {
    const thumb = e.target.closest('.lcp-thumbs button');
    if (thumb) {
      const gallery = thumb.closest('.lcp-gallery');
      const img = gallery.querySelector('.lcp-stage > img');
      gallery.querySelector('.is-selected')?.classList.remove('is-selected');
      thumb.classList.add('is-selected');
      if (img) { img.removeAttribute('srcset'); img.src = thumb.dataset.src; img.alt = thumb.dataset.alt || ''; }
      return;
    }
    const step = e.target.closest('[data-lc-qty]');
    if (step) {
      e.preventDefault();
      const input = step.parentElement.querySelector('input');
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(step.dataset.lcQty));
      updateBuyLabel(input.form);
      return;
    }
    const code = e.target.closest('.lcp-coupon__code');
    if (code) {
      const text = code.dataset.code, ok = code.parentElement.querySelector('.lcp-coupon__ok');
      const done = () => { if (ok) { ok.textContent = 'Copied!'; setTimeout(() => { ok.textContent = 'Copy code'; }, 1800); } };
      if (navigator.clipboard) navigator.clipboard.writeText(text).then(done, done); else done();
    }
  });
  document.addEventListener('input', e => { if (e.target.matches('.lcp-qty input')) updateBuyLabel(e.target.form); });
  document.addEventListener('change', e => {
    const select = e.target.closest('.lcp-variant');
    if (!select) return;
    const opt = select.selectedOptions[0], btn = select.form.querySelector('[data-lc-label]');
    if (!btn) return;
    btn.dataset.lcUnit = opt.dataset.price;
    btn.disabled = opt.dataset.available !== 'true';
    if (btn.disabled) btn.textContent = btn.dataset.lcSoldout; else updateBuyLabel(select.form);
  });
})();
